import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getApiUrl, API_ENDPOINTS } from '../config/api';

const MONTH_NAMES_LT = [
  'Sausis', 'Vasaris', 'Kovas', 'Balandis', 'Gegužė', 'Birželis',
  'Liepa', 'Rugpjūtis', 'Rugsėjis', 'Spalis', 'Lapkritis', 'Gruodis'
];

type PeriodMode = 'month' | 'range' | 'all';

interface ActivityReportProgram {
  type: 'excursions' | 'edukacijos';
  title: string;
  sessionCount: number;
  totalParticipants: number;
  colleagues: string[];
}

interface ActivityReportColleague {
  colleagueId: string | null;
  name: string;
  excursionCount: number;
  edukationCount: number;
  sessionCount: number;
  totalParticipants: number;
  programs: string[];
}

interface ActivityReportEvent {
  type: 'excursions' | 'edukacijos';
  id: string;
  title: string;
  eventDate: string;
  startTime: string;
  endTime: string;
  location: string | null;
  maxParticipants: number;
  status: string;
  colleagues: string[];
}

interface ActivityReportPeriod {
  mode: PeriodMode;
  month?: string;
  startMonth?: string;
  endMonth?: string;
  startDate: string | null;
  endDate: string | null;
}

interface ActivityReport {
  period: ActivityReportPeriod;
  totals: {
    excursionSessions: number;
    edukationSessions: number;
    totalParticipants: number;
    totalSessions: number;
  };
  byProgram: ActivityReportProgram[];
  byColleague: ActivityReportColleague[];
  events: ActivityReportEvent[];
}

function dateToMonthStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function MonthYearPicker({
  label,
  value,
  onChange
}: {
  label: string;
  value: Date;
  onChange: (d: Date) => void;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <FormControl sx={{ minWidth: 140 }} size="small">
        <InputLabel>{label}</InputLabel>
        <Select
          label={label}
          value={value.getMonth()}
          onChange={(e) => onChange(new Date(value.getFullYear(), Number(e.target.value), 1))}
        >
          {MONTH_NAMES_LT.map((name, i) => (
            <MenuItem key={name} value={i}>{name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 100 }} size="small">
        <InputLabel>Metai</InputLabel>
        <Select
          label="Metai"
          value={value.getFullYear()}
          onChange={(e) => onChange(new Date(Number(e.target.value), value.getMonth(), 1))}
        >
          {Array.from({ length: 11 }, (_, i) => new Date().getFullYear() - 5 + i).map((y) => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}

const ExhibitionReports: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation();
  const [periodMode, setPeriodMode] = useState<PeriodMode>('month');
  const [reportMonth, setReportMonth] = useState<Date>(new Date());
  const [rangeStart, setRangeStart] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 2, 1);
  });
  const [rangeEnd, setRangeEnd] = useState<Date>(new Date());
  const [reportType, setReportType] = useState<'all' | 'excursions' | 'edukacijos'>('all');
  const [reportStatus, setReportStatus] = useState<'completed' | 'active' | 'all'>('completed');
  const [report, setReport] = useState<ActivityReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const rangeInvalid = periodMode === 'range' && dateToMonthStr(rangeStart) > dateToMonthStr(rangeEnd);

  const buildQueryString = useCallback(() => {
    const params = new URLSearchParams();
    params.set('type', reportType);
    params.set('status', reportStatus);

    if (periodMode === 'all') {
      params.set('period', 'all');
    } else if (periodMode === 'range') {
      params.set('startMonth', dateToMonthStr(rangeStart));
      params.set('endMonth', dateToMonthStr(rangeEnd));
    } else {
      params.set('month', dateToMonthStr(reportMonth));
    }

    return params.toString();
  }, [periodMode, reportMonth, rangeStart, rangeEnd, reportType, reportStatus]);

  const fetchReport = useCallback(async () => {
    if (rangeInvalid) {
      setError(t('exhibition.reports.periodInvalidRange', 'Pradžios mėnuo negali būti vėlesnis už pabaigos mėnesį'));
      setReport(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const url = `${getApiUrl(API_ENDPOINTS.EXHIBITION_ACTIVITY_REPORT)}?${buildQueryString()}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || t('exhibition.reports.loadFailed', 'Failed to load report'));
      }

      const data = await response.json();
      setReport(data);
    } catch (err) {
      setReport(null);
      setError(err instanceof Error ? err.message : t('exhibition.reports.loadFailed', 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  }, [buildQueryString, rangeInvalid, t]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchReport();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchReport]);

  const periodLabel = useMemo(() => {
    if (report?.period) {
      const p = report.period;
      if (p.mode === 'all') {
        return t('exhibition.reports.periodAllLabel', 'Visi laikotarpiai');
      }
      if (p.mode === 'range' && p.startMonth && p.endMonth) {
        return `${p.startMonth} – ${p.endMonth}`;
      }
      if (p.month) {
        return p.month;
      }
      if (p.startDate && p.endDate) {
        return `${p.startDate} – ${p.endDate}`;
      }
    }
    if (periodMode === 'all') {
      return t('exhibition.reports.periodAllLabel', 'Visi laikotarpiai');
    }
    if (periodMode === 'range') {
      return `${dateToMonthStr(rangeStart)} – ${dateToMonthStr(rangeEnd)}`;
    }
    return dateToMonthStr(reportMonth);
  }, [report, periodMode, rangeStart, rangeEnd, reportMonth, t]);

  const formatEventDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('lt-LT');
  };

  const formatTime = (value: string) => {
    if (!value) return '';
    return String(value).slice(0, 5);
  };

  const typeLabel = (type: 'excursions' | 'edukacijos') =>
    type === 'excursions'
      ? t('exhibition.reports.typeExcursions', 'Ekskursijos')
      : t('exhibition.reports.typeEdukacijos', 'Edukacijos');

  const statusLabel = (status: string) => {
    const key = `excursions.status.${status}`;
    const translated = t(key, status);
    return translated === key ? status : translated;
  };

  const colleagueDisplayName = (name: string) =>
    name || t('exhibition.reports.unassigned', 'Nepriskirta');

  return (
    <Box sx={{ mb: 4 }}>
      {!embedded && (
        <Typography variant="h5" gutterBottom>
          {t('exhibition.reports.title', 'Veiklos ataskaita')}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('exhibition.reports.subtitle', 'Ekskursijų ir edukacijų suvestinė pagal programą ir kolegas. Dalyvių skaičius – pagal maksimalų dalyvių skaičių.')}
        {' '}
        <strong>{periodLabel}</strong>
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('exhibition.reports.filters', 'Filtrai')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 2, mb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('exhibition.reports.periodMode', 'Laikotarpis')}</InputLabel>
              <Select
                label={t('exhibition.reports.periodMode', 'Laikotarpis')}
                value={periodMode}
                onChange={(e) => setPeriodMode(e.target.value as PeriodMode)}
              >
                <MenuItem value="month">{t('exhibition.reports.periodMonth', 'Vienas mėnuo')}</MenuItem>
                <MenuItem value="range">{t('exhibition.reports.periodRange', 'Laikotarpis')}</MenuItem>
                <MenuItem value="all">{t('exhibition.reports.periodAll', 'Visi')}</MenuItem>
              </Select>
            </FormControl>

            {periodMode === 'month' && (
              <MonthYearPicker
                label={t('exhibition.reports.month', 'Mėnuo')}
                value={reportMonth}
                onChange={setReportMonth}
              />
            )}

            {periodMode === 'range' && (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                <MonthYearPicker
                  label={t('exhibition.reports.periodFrom', 'Nuo')}
                  value={rangeStart}
                  onChange={setRangeStart}
                />
                <MonthYearPicker
                  label={t('exhibition.reports.periodTo', 'Iki')}
                  value={rangeEnd}
                  onChange={setRangeEnd}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('exhibition.reports.type', 'Tipas')}</InputLabel>
              <Select
                label={t('exhibition.reports.type', 'Tipas')}
                value={reportType}
                onChange={(e) => setReportType(e.target.value as typeof reportType)}
              >
                <MenuItem value="all">{t('exhibition.reports.typeAll', 'Visi')}</MenuItem>
                <MenuItem value="excursions">{t('exhibition.reports.typeExcursions', 'Ekskursijos')}</MenuItem>
                <MenuItem value="edukacijos">{t('exhibition.reports.typeEdukacijos', 'Edukacijos')}</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>{t('exhibition.reports.status', 'Būsena')}</InputLabel>
              <Select
                label={t('exhibition.reports.status', 'Būsena')}
                value={reportStatus}
                onChange={(e) => setReportStatus(e.target.value as typeof reportStatus)}
              >
                <MenuItem value="completed">{t('exhibition.reports.statusCompleted', 'Užbaigti')}</MenuItem>
                <MenuItem value="active">{t('exhibition.reports.statusActive', 'Visi aktyvūs')}</MenuItem>
                <MenuItem value="all">{t('exhibition.reports.statusAll', 'Visi (įsk. atšaukti)')}</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button
                variant="outlined"
                onClick={fetchReport}
                disabled={loading || rangeInvalid}
                startIcon={loading ? <CircularProgress size={18} /> : <RefreshIcon />}
              >
                {t('exhibition.reports.refresh', 'Atnaujinti')}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {loading && !report && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {report && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">{t('exhibition.reports.totalSessions', 'Viso sesijų')}</Typography>
                <Typography variant="h4">{report.totals.totalSessions}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">{t('exhibition.reports.totalParticipants', 'Dalyvių suma')}</Typography>
                <Typography variant="h4">{report.totals.totalParticipants}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">{t('exhibition.reports.excursionSessions', 'Ekskursijos')}</Typography>
                <Typography variant="h4">{report.totals.excursionSessions}</Typography>
              </CardContent>
            </Card>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="body2" color="text.secondary">{t('exhibition.reports.edukationSessions', 'Edukacijos')}</Typography>
                <Typography variant="h4">{report.totals.edukationSessions}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('exhibition.reports.byProgram', 'Programų suvestinė')}
              </Typography>
              {report.byProgram.length === 0 ? (
                <Typography color="text.secondary">{t('exhibition.reports.noData', 'Pasirinktam laikotarpiui duomenų nėra')}</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('exhibition.reports.colType', 'Tipas')}</TableCell>
                        <TableCell>{t('exhibition.reports.colProgram', 'Programa')}</TableCell>
                        <TableCell align="right">{t('exhibition.reports.colSessions', 'Sesijų sk.')}</TableCell>
                        <TableCell align="right">{t('exhibition.reports.colParticipants', 'Dalyvių suma')}</TableCell>
                        <TableCell>{t('exhibition.reports.colColleagues', 'Kolegos')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.byProgram.map((row) => (
                        <TableRow key={`${row.type}-${row.title}`}>
                          <TableCell>
                            <Chip label={typeLabel(row.type)} size="small" color={row.type === 'excursions' ? 'primary' : 'secondary'} />
                          </TableCell>
                          <TableCell>{row.title}</TableCell>
                          <TableCell align="right">{row.sessionCount}</TableCell>
                          <TableCell align="right">{row.totalParticipants}</TableCell>
                          <TableCell>{row.colleagues.length > 0 ? row.colleagues.join(', ') : t('exhibition.reports.unassigned', 'Nepriskirta')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('exhibition.reports.byColleague', 'Kolegų suvestinė')}
              </Typography>
              {report.byColleague.length === 0 ? (
                <Typography color="text.secondary">{t('exhibition.reports.noData', 'Pasirinktam laikotarpiui duomenų nėra')}</Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('exhibition.reports.colColleague', 'Kolega')}</TableCell>
                        <TableCell align="right">{t('exhibition.reports.colExcursions', 'Ekskursijų')}</TableCell>
                        <TableCell align="right">{t('exhibition.reports.colEdukacijos', 'Edukacijų')}</TableCell>
                        <TableCell align="right">{t('exhibition.reports.colSessions', 'Sesijų sk.')}</TableCell>
                        <TableCell align="right">{t('exhibition.reports.colParticipants', 'Dalyvių suma')}</TableCell>
                        <TableCell>{t('exhibition.reports.colProgram', 'Programos')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.byColleague.map((row) => (
                        <TableRow key={row.colleagueId || 'unassigned'}>
                          <TableCell>{colleagueDisplayName(row.name)}</TableCell>
                          <TableCell align="right">{row.excursionCount}</TableCell>
                          <TableCell align="right">{row.edukationCount}</TableCell>
                          <TableCell align="right">{row.sessionCount}</TableCell>
                          <TableCell align="right">{row.totalParticipants}</TableCell>
                          <TableCell>{row.programs.join(', ')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6">
                  {t('exhibition.reports.details', 'Išsamus sąrašas')}
                </Typography>
                <IconButton onClick={() => setDetailsOpen((prev) => !prev)} aria-label="toggle details">
                  {detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>
              <Collapse in={detailsOpen}>
                {report.events.length === 0 ? (
                  <Typography color="text.secondary" sx={{ mt: 1 }}>
                    {t('exhibition.reports.noData', 'Pasirinktam laikotarpiui duomenų nėra')}
                  </Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('exhibition.reports.colType', 'Tipas')}</TableCell>
                          <TableCell>{t('exhibition.reports.colProgram', 'Programa')}</TableCell>
                          <TableCell>{t('exhibition.reports.colDate', 'Data')}</TableCell>
                          <TableCell>{t('exhibition.reports.colTime', 'Laikas')}</TableCell>
                          <TableCell>{t('exhibition.reports.colLocation', 'Vieta')}</TableCell>
                          <TableCell align="right">{t('exhibition.reports.colParticipants', 'Dalyvių')}</TableCell>
                          <TableCell>{t('exhibition.reports.status', 'Būsena')}</TableCell>
                          <TableCell>{t('exhibition.reports.colColleagues', 'Kolegos')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.events.map((row) => (
                          <TableRow key={`${row.type}-${row.id}`}>
                            <TableCell>
                              <Chip label={typeLabel(row.type)} size="small" />
                            </TableCell>
                            <TableCell>{row.title}</TableCell>
                            <TableCell>{formatEventDate(row.eventDate)}</TableCell>
                            <TableCell>{`${formatTime(row.startTime)} – ${formatTime(row.endTime)}`}</TableCell>
                            <TableCell>{row.location || '—'}</TableCell>
                            <TableCell align="right">{row.maxParticipants}</TableCell>
                            <TableCell>{statusLabel(row.status)}</TableCell>
                            <TableCell>
                              {row.colleagues.length > 0 ? row.colleagues.join(', ') : t('exhibition.reports.unassigned', 'Nepriskirta')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Collapse>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default ExhibitionReports;
